import React from "react";
import * as _ from "underscore";
import Accordion from "web-ui/components/internal/Accordion";
import Button from "web-ui/components/internal/Button";
import Checkbox from "web-ui/components/internal/Checkbox";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Menu from "web-ui/components/internal/Menu";
import MenuItem from "web-ui/components/internal/MenuItem";
import Pagination from "web-ui/components/internal/Pagination";
import Select from "web-ui/components/internal/Select";
import TextInput from "web-ui/components/internal/TextInput";
import useAwaitedValue from "web-ui/components/useAwaitedValue";

import {
  type VoiceListVoice,
  getCombinedVoiceList,
} from "../../api/generalTts";
import type {
  TtsGenerationAge,
  TtsGenerationGender,
} from "../../api/ttsGenerationBackend";
import { getDisplayLanguageName } from "../../util/languageUtils";

import VoiceCard from "./VoiceCard";
import VoiceDrawer from "./VoiceDrawer";
import styles from "./VoiceFinder.module.scss";

const PER_PAGE = 13;

type Filter = (voice: VoiceListVoice) => boolean;

type SortField = "language" | "newest";

export const formatCharacter = (character: string) =>
  character === "CHARACTER_UNKNOWN" ? "Other" : titleCaseFormatter(character);

export const formatLanguage = (...languages: string[]) =>
  `${languages
    .slice(0, 2)
    .map(lang => `${getDisplayLanguageName(lang)} (${lang})`)
    .join(", ")}${languages.length > 2 ? ` +${languages.length - 2}` : ""}`;

export const formatProvider = (provider: string) => {
  switch (provider) {
    case "AMAZON":
      return "Amazon";
    case "AZURE":
      return "Azure";
    case "ELEVENLABS":
      return "ElevenLabs";
    case "GOOGLE":
      return "Google";
    case "RECORDED":
      return "Recorded";
  }
  return provider;
};

export const titleCaseFormatter = (value: string) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;

const CheckboxList = ({
  children,
  double,
}: {
  children: React.ReactNode;
  double?: boolean;
}) => (
  <div className={double ? styles["checkboxes-double"] : styles.checkboxes}>
    {children}
  </div>
);

const CheckboxOptions = ({
  double,
  format = value => value,
  onChange,
  options,
  value,
}: {
  double?: boolean;
  format?: (value: string) => string;
  onChange?: (newValue: string[]) => void;
  options: string[];
  value: string[];
}) => (
  <CheckboxList double={double}>
    {options.map(o => (
      <Checkbox
        checked={value.includes(o)}
        key={o}
        onChange={e => {
          if (e.checked && !value.includes(o)) {
            onChange?.([...value, o]);
          } else if (!e.checked && value.includes(o)) {
            onChange?.(value.filter(v => v !== o));
          }
        }}
      >
        {format(o)}
      </Checkbox>
    ))}
  </CheckboxList>
);

const SortMenu = ({
  onChange,
  value,
}: {
  onChange?: (newValue: SortField) => void;
  value: SortField;
}) => (
  <Select
    leadingVisual={iconFactory("sort")}
    onChange={e => onChange?.(e.value as SortField)}
    value={value}
  >
    <option value="language">Language (A to Z)</option>
    <option value="newest">Newest</option>
  </Select>
);

const VoiceFinder = () => {
  const [accordionKey, rerenderAccordion] = React.useReducer(
    value => value + 1,
    0,
  );

  const [filterAges, setFilterAges] = React.useState<string[]>([]);
  const [filterCharacters, setFilterCharacters] = React.useState<string[]>([]);
  const [filterGenders, setFilterGenders] = React.useState<string[]>([]);
  const [filterIsAvailable, setFilterIsAvailable] = React.useState(false);
  const [filterIsExpressive, setFilterIsExpressive] = React.useState(false);
  const [filterIsMultilingual, setFilterIsMultilingual] = React.useState(false);
  const [filterIsUnavailable, setFilterIsUnavailable] = React.useState(false);
  const [filterLanguages, setFilterLanguages] = React.useState<string[]>([]);
  const [filterMissingTags, setFilterMissingTags] = React.useState(false);
  const [filterProviders, setFilterProviders] = React.useState<string[]>([]);
  const [filterUsedAtDuolingo, setFilterUsedAtDuolingo] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<VoiceListVoice>();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [sortBy, setSortBy] = React.useState<SortField>("language");

  const data = useAwaitedValue([], () => getCombinedVoiceList(), []);

  const ageOptions = React.useMemo(
    () =>
      _.uniq(data.map(voice => voice.age))
        .filter((o): o is TtsGenerationAge => o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const characterOptions = React.useMemo(
    () =>
      _.uniq(data.map(voice => voice.character ?? "CHARACTER_UNKNOWN")).sort(
        (a, b) => formatCharacter(a).localeCompare(formatCharacter(b)),
      ),
    [data],
  );

  const genderOptions = React.useMemo(
    () =>
      _.uniq(data.map(voice => voice.gender))
        .filter((o): o is TtsGenerationGender => o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const languageOptions = React.useMemo(
    () =>
      _.uniq(data.map(voice => voice.languageId)).sort((a, b) =>
        formatLanguage(a).localeCompare(formatLanguage(b)),
      ),
    [data],
  );

  const providerOptions = React.useMemo(
    () =>
      _.uniq(data.map(voice => voice.provider)).sort((a, b) =>
        formatProvider(a).localeCompare(formatProvider(b)),
      ),
    [data],
  );

  const [filters, filterCount] = React.useMemo(() => {
    const newFilters: Filter[] = [];
    let newCount = 0;
    if (filterAges.length) {
      newFilters.push(
        voice => voice.age !== undefined && filterAges.includes(voice.age),
      );
      newCount += filterAges.length;
    }
    if (filterCharacters.length) {
      newFilters.push(voice =>
        filterCharacters.includes(voice.character ?? "CHARACTER_UNKNOWN"),
      );
      newCount += filterCharacters.length;
    }
    if (filterGenders.length) {
      newFilters.push(
        voice =>
          voice.gender !== undefined && filterGenders.includes(voice.gender),
      );
      newCount += filterGenders.length;
    }
    if (filterIsAvailable) {
      newFilters.push(voice => voice.status !== "UNAVAILABLE");
      newCount++;
    }
    if (filterIsExpressive) {
      newFilters.push(voice => voice.isExpressive);
      newCount++;
    }
    if (filterIsMultilingual) {
      newFilters.push(voice => voice.isMultilingual);
      newCount++;
    }
    if (filterIsUnavailable) {
      newFilters.push(voice => voice.status === "UNAVAILABLE");
      newCount++;
    }
    if (filterLanguages.length) {
      newFilters.push(voice =>
        filterLanguages.some(
          lang =>
            lang === voice.languageId ||
            voice.languageStatuses.some(status => status.language === lang),
        ),
      );
      newCount += filterLanguages.length;
    }
    if (filterMissingTags) {
      // TODO: how to implement this?
      newFilters.push(() => true);
      newCount++;
    }
    if (filterProviders.length) {
      newFilters.push(voice => filterProviders.includes(voice.provider));
      newCount += filterProviders.length;
    }
    if (filterUsedAtDuolingo) {
      newFilters.push(voice => voice.isUsedInLearningApp);
      newCount++;
    }
    return [newFilters, newCount];
  }, [
    filterAges,
    filterCharacters,
    filterGenders,
    filterIsAvailable,
    filterIsExpressive,
    filterIsMultilingual,
    filterIsUnavailable,
    filterLanguages,
    filterMissingTags,
    filterProviders,
    filterUsedAtDuolingo,
  ]);

  const filteredData = React.useMemo(
    () =>
      data
        .filter(
          voice =>
            filters.every(fn => fn(voice)) &&
            (query === "" ||
              voice.alias?.toLowerCase().includes(query.toLowerCase()) ||
              voice.name.toLowerCase().includes(query.toLowerCase())),
        )
        .sort((a, b) => {
          if (sortBy === "language") {
            return formatLanguage(a.languageId).localeCompare(
              formatLanguage(b.languageId),
            );
          } else if (sortBy === "newest") {
            return (b.addedAt?.valueOf() ?? 0) - (a.addedAt?.valueOf() ?? 0);
          }
          return 0;
        }),
    [data, filters, query, sortBy],
  );

  const handleReset = () => {
    setFilterAges([]);
    setFilterCharacters([]);
    setFilterGenders([]);
    setFilterIsAvailable(false);
    setFilterIsExpressive(false);
    setFilterIsMultilingual(false);
    setFilterIsUnavailable(false);
    setFilterLanguages([]);
    setFilterMissingTags(false);
    setFilterProviders([]);
    setFilterUsedAtDuolingo(true);
    // Resets the expanded panels.
    rerenderAccordion();
  };

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <TextInput
          onChange={e => {
            handleReset();
            setQuery(e.value);
          }}
          placeholder="Search by voice name"
          trailingVisual={iconFactory("search")}
          type="search"
          value={query}
        />
        <div className={styles.menu}>
          <span className={styles["menu-name"]}>Collections</span>
          <Menu multiple={true}>
            <MenuItem state="selected">All</MenuItem>
            <MenuItem state="disabled">One-word wonders</MenuItem>
            <MenuItem state="disabled">Longform lounge</MenuItem>
            <MenuItem state="disabled">Most expressive</MenuItem>
            <MenuItem state="disabled">Real time rockstars</MenuItem>
          </Menu>
        </div>
        <div className={styles.menu2}>
          <span className={styles["menu-header"]}>
            <span className={styles["menu-name"]}>Filters</span>
            <span className={styles.count}>{filterCount}</span>
          </span>
          <Accordion
            bordered="in-container"
            inset={false}
            items={[
              {
                content: (
                  <>
                    <Select
                      className={styles.select}
                      onChange={e =>
                        setFilterLanguages(e.value ? [e.value] : [])
                      }
                      value={filterLanguages[0] ?? ""}
                    >
                      <option value="">Select language</option>
                      {languageOptions.map(lang => (
                        <option key={lang} value={lang}>
                          {formatLanguage(lang)}
                        </option>
                      ))}
                    </Select>
                    <CheckboxList>
                      <Checkbox
                        checked={filterUsedAtDuolingo}
                        onChange={e => setFilterUsedAtDuolingo(e.checked)}
                      >
                        Used at Duolingo
                      </Checkbox>
                    </CheckboxList>
                  </>
                ),
                defaultExpanded: true,
                header: "Language",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={formatCharacter}
                    onChange={newValue => setFilterCharacters(newValue)}
                    options={characterOptions}
                    value={filterCharacters}
                  />
                ),
                header: "Character",
              },
              {
                content: (
                  <CheckboxOptions
                    format={titleCaseFormatter}
                    onChange={newValue => setFilterGenders(newValue)}
                    options={genderOptions}
                    value={filterGenders}
                  />
                ),
                header: "Gender",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={titleCaseFormatter}
                    onChange={newValue => setFilterAges(newValue)}
                    options={ageOptions}
                    value={filterAges}
                  />
                ),
                header: "Age",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsMultilingual}
                      onChange={e => setFilterIsMultilingual(e.checked)}
                    >
                      Multilingual
                    </Checkbox>
                    <Checkbox
                      checked={filterIsExpressive}
                      onChange={e => setFilterIsExpressive(e.checked)}
                    >
                      Expressive
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Voice features",
              },
              {
                content: (
                  <CheckboxOptions
                    format={formatProvider}
                    onChange={newValue => setFilterProviders(newValue)}
                    options={providerOptions}
                    value={filterProviders}
                  />
                ),
                header: "Provider",
              },
              {
                content: <>This section hasn&apos;t been implemented yet</>,
                header: "Quality",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsAvailable}
                      onChange={e => setFilterIsAvailable(e.checked)}
                    >
                      Available
                    </Checkbox>
                    <Checkbox
                      checked={filterIsUnavailable}
                      onChange={e => setFilterIsUnavailable(e.checked)}
                    >
                      Unavailable
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Status",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterMissingTags}
                      onChange={e => setFilterMissingTags(e.checked)}
                    >
                      Missing tags
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Tags",
              },
            ]}
            key={accordionKey}
            textStyle="uppercase"
          />
          <Button
            className={styles.reset}
            onClick={handleReset}
            size="s"
            variant="secondary"
          >
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.h1}>
            {filteredData.length === data.length ? "All" : filteredData.length}{" "}
            Voices
          </h1>
          <div className={styles["header-total"]}>
            {data.length}{" "}
            {filteredData.length === data.length ? "voices" : "total"}
          </div>
          <div className={styles["header-buttons"]}>
            <SortMenu
              onChange={newValue => setSortBy(newValue)}
              value={sortBy}
            />
            <Button
              leadingVisual={<Icon color="gray-600" name="refresh" />}
              variant="secondary"
            >
              Sync
            </Button>
          </div>
        </div>
        <div className={styles.list}>
          {filteredData
            .slice((page - 1) * PER_PAGE, page * PER_PAGE)
            .map(voice => (
              <VoiceCard
                key={voice.name}
                onSelect={() => {
                  setSelected(voice);
                  setShowDrawer(true);
                }}
                state={voice === selected ? "selected" : "enabled"}
                voice={voice}
              />
            ))}
        </div>
        {filteredData.length ? (
          <div className={styles.footer}>
            <Pagination
              className={styles.pagination}
              onChange={newPage => setPage(newPage)}
              page={page}
              totalPages={Math.ceil(filteredData.length / PER_PAGE)}
            />
          </div>
        ) : null}
      </div>
      <VoiceDrawer
        className={styles.drawer}
        onRequestClose={() => setShowDrawer(false)}
        open={showDrawer}
        voice={selected}
      />
    </div>
  );
};

export default VoiceFinder;
